import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCheck, Loader2 } from 'lucide-react';
import type { PanelDeIdentificacion } from '../../services/carga-manual-service';

/** Los estados por los que se puede filtrar (§4.3). */
const ESTADOS = [
  { id: 'TODOS', texto: 'Todos' },
  { id: 'SIN_DECIDIR', texto: 'Sin decidir' },
  { id: 'RECONOCIDO', texto: 'Reconocidos' },
  { id: 'NUEVO_MARCADO', texto: 'Nuevos' },
  { id: 'RECHAZADO', texto: 'Rechazados' },
] as const;

/** Concordancia de número: «1 renglón» y no «1 renglones». */
const plural = (n: number, singular: string, plural: string) => (n === 1 ? singular : plural);

export type FiltrosVisibles = {
  estado: string;
  busqueda: string;
  confianzaMin?: number;
  confianzaMax?: number;
};

interface FiltrosProps {
  valor: FiltrosVisibles;
  onCambiar: (v: FiltrosVisibles) => void;
}

export function FiltrosDelPanel({ valor, onCambiar }: FiltrosProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <Input
        value={valor.busqueda}
        onChange={(e) => onCambiar({ ...valor, busqueda: e.target.value })}
        placeholder="Buscar un renglón…"
        className="h-8 w-56"
      />

      {ESTADOS.map((e) => (
        <Button
          key={e.id}
          size="sm"
          variant={valor.estado === e.id ? 'default' : 'outline'}
          className="h-7"
          onClick={() => onCambiar({ ...valor, estado: e.id })}
        >
          {e.texto}
        </Button>
      ))}

      <div className="flex items-center gap-1 ml-2">
        <span className="text-xs text-muted-foreground">Confianza</span>
        <Input
          type="number"
          min={0}
          max={100}
          value={valor.confianzaMin ?? ''}
          onChange={(e) =>
            onCambiar({
              ...valor,
              confianzaMin: e.target.value ? Number(e.target.value) : undefined,
            })
          }
          className="h-7 w-16"
          placeholder="mín"
        />
        <Input
          type="number"
          min={0}
          max={100}
          value={valor.confianzaMax ?? ''}
          onChange={(e) =>
            onCambiar({
              ...valor,
              confianzaMax: e.target.value ? Number(e.target.value) : undefined,
            })
          }
          className="h-7 w-16"
          placeholder="máx"
        />
      </div>
    </div>
  );
}

interface BarraAccionesProps {
  panel: PanelDeIdentificacion;
  trabajando: boolean;
  onConfirmarDesde: (confianza: number) => void;
  onCerrar: () => void;
}

export function BarraAcciones({
  panel,
  trabajando,
  onConfirmarDesde,
  onCerrar,
}: BarraAccionesProps) {
  const [desde, setDesde] = useState(90);
  const c = panel.contadores;
  const lista = panel.job.estado === 'LISTA';

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-md border p-3 mb-3">
      {/* Contadores arriba, para saber cuánto falta sin contar a mano (§4.3) */}
      <div className="flex flex-wrap gap-4 text-sm">
        <span>
          <strong>{c.total}</strong> {plural(c.total, 'renglón', 'renglones')}
        </span>
        <span className="text-green-700">
          <strong>{c.reconocidos}</strong> {plural(c.reconocidos, 'reconocido', 'reconocidos')}
        </span>
        <span className="text-yellow-700">
          <strong>{c.sinDecidir}</strong> sin decidir
        </span>
        <span className="text-blue-700">
          <strong>{c.nuevos}</strong> {plural(c.nuevos, 'nuevo', 'nuevos')}
        </span>
        {c.rechazados > 0 && (
          <span className="text-red-700">
            <strong>{c.rechazados}</strong> {plural(c.rechazados, 'rechazado', 'rechazados')}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <span className="text-sm text-muted-foreground">Confirmar todo desde</span>
        <Input
          type="number"
          min={0}
          max={100}
          value={desde}
          onChange={(e) => setDesde(Number(e.target.value))}
          className="h-8 w-20"
          disabled={trabajando || lista}
        />
        <span className="text-sm text-muted-foreground">%</span>
        <Button
          size="sm"
          variant="outline"
          disabled={trabajando || lista}
          onClick={() => onConfirmarDesde(desde)}
        >
          {trabajando ? (
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          ) : (
            <CheckCheck className="w-3 h-3 mr-1" />
          )}
          Aplicar
        </Button>
      </div>

      {/*
        ⚖️ El botón NO se deshabilita por `puedeCerrarse`. Se deja apretable a
        propósito para que la advertencia del §4.2 pueda aparecer y decir
        exactamente qué falta. Un botón gris sin explicación deja a la persona
        sin saber qué le falta.
      */}
      <Button size="sm" onClick={onCerrar} disabled={trabajando || lista}>
        {lista ? 'Carga lista' : 'Dar la carga por lista'}
      </Button>
    </div>
  );
}
